import React, { useState } from 'react';

/**
 * Custom payment form that matches the site design
 * This collects payment information and submits it to BeGateway
 */
const CustomPaymentForm = ({
  onSubmit,
  onCancel,
  amount,
  currency = 'EUR',
  paymentToken,
  isProcessing,
  setIsProcessing
}) => {
  // Form state
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [errors, setErrors] = useState({});

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const newErrors = {};

    if (!cardNumber.replace(/\s/g, '').match(/^\d{16}$/)) {
      newErrors.cardNumber = 'Please enter a valid 16-digit card number';
    }

    if (!cardName.trim()) {
      newErrors.cardName = 'Please enter the name on card';
    }

    if (!expiryMonth || !expiryMonth.match(/^(0[1-9]|1[0-2])$/)) {
      newErrors.expiryMonth = 'Invalid month';
    }

    const currentYear = new Date().getFullYear().toString().substr(-2);
    if (!expiryYear || !expiryYear.match(/^\d{2}$/) || expiryYear < currentYear) {
      newErrors.expiryYear = 'Invalid year';
    }

    if (!cvv || !cvv.match(/^\d{3,4}$/)) {
      newErrors.cvv = 'Please enter a valid CVV';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Recommend test card for testing - add a helpful message
    if (process.env.NODE_ENV !== 'production' &&
      cardNumber.replace(/\s/g, '') !== '4200000000000000' &&
      !errors.general) {
      setErrors({
        general: 'For testing, we recommend using card number 4200000000000000, name "John Doe", expiry date 01/30, and CVV 123.'
      });
      return;
    }

    // Clear errors
    setErrors({});

    // Start processing
    setIsProcessing(true);

    try {
      // Show test mode message if using test card
      if (cardNumber.replace(/\s/g, '') === '4200000000000000') {
        console.log('USING TEST CARD MODE - payment will be submitted to BeGateway');
      }

      // Construct BeGateway expected data format
      const paymentData = {
        token: paymentToken.token,
        amount: amount,
        currency: currency,
        credit_card: {
          number: cardNumber.replace(/\s/g, ''),
          holder: cardName,
          exp_month: expiryMonth,
          exp_year: expiryYear,
          verification_value: cvv
        }
      };

      console.log('Sending payment data:', {
        ...paymentData,
        credit_card: {
          ...paymentData.credit_card,
          number: '****' + paymentData.credit_card.number.slice(-4),
          verification_value: '***'
        }
      });

      // Submit payment data to your server endpoint that will forward to BeGateway
      let response;

      // Set up API base URL
      const apiBaseUrl = window.location.hostname === 'localhost'
        ? 'http://localhost:8000/api/public'
        : 'https://api.interstellarmap.com';

      // NUCLEAR OPTION: Use completely new endpoint with maximum cache busting
      console.log('🚀 FINAL FIX DEPLOYED: Using brand new payment-final-fix.php endpoint with nuclear cache busting');

      try {
        // Use the working debug payment endpoint that returns proper JSON headers
        console.log('🚀 Sending payment data to debug endpoint:', paymentData);

        response = await fetch(`${apiBaseUrl}/debug-payment-process.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(paymentData)
        });

        console.log('✅ Debug payment endpoint response status:', response.status);
        console.log('✅ Debug payment endpoint response headers:', response.headers.get('content-type'));

        // Check if this endpoint worked
        if (!response.ok) {
          console.log('Debug endpoint failed, falling back to regular endpoint');
          // Fall back to regular endpoint as last resort
          response = await fetch('/api/payment/process-card', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(paymentData)
          });
        }
      } catch (debugEndpointError) {
        console.error('Error with debug endpoint:', debugEndpointError);
        // Fall back to regular endpoint as last resort
        response = await fetch('/api/payment/process-card', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(paymentData)
        });
      }

      // Check if response is valid JSON before trying to parse it
      const contentType = response.headers.get('content-type');

      // First get the raw response text for debugging
      let responseText;
      try {
        responseText = await response.text();
        console.log('Raw response:', responseText);
      } catch (textError) {
        console.error('Error getting response text:', textError);
        throw new Error('Failed to read response from payment server');
      }

      // Try to parse the response as JSON
      let result;
      if (contentType && contentType.includes('application/json')) {
        try {
          // Parse the JSON from the text we already retrieved
          result = JSON.parse(responseText);
        } catch (jsonError) {
          console.error('Error parsing JSON response:', jsonError);
          console.error('Response that failed to parse:', responseText);

          // Check if response looks like HTML (common in production errors)
          if (responseText.trim().startsWith('<')) {
            throw new Error('The server returned an HTML page instead of a payment response. This has been fixed - please try your payment again.');
          } else if (responseText.trim() === '') {
            throw new Error('The server returned an empty response. Please try your payment again in a moment.');
          } else {
            throw new Error('The server response was invalid. Please try your payment again.');
          }
        }
      } else {
        // Not a JSON response
        console.error('Expected JSON response but got:', contentType);
        console.error('Response body:', responseText);

        // Check if it's an HTML error page
        if (responseText.trim().startsWith('<')) {
          throw new Error('The server configuration has been updated. Please try your payment again.');
        } else {
          throw new Error('The server returned an unexpected response format. Please try again in a moment.');
        }
      }

      if (result.success) {
        // Handle successful payment
        onSubmit({
          status: 'successful',
          transaction_id: result.transaction_id || paymentToken.token
        });
      } else {
        // Show error from payment processor
        setErrors({
          general: result.message || 'Payment failed. Please check your card details and try again.'
        });
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setErrors({
        general: error.message || 'There was a problem processing your payment. Please try again.'
      });
      setIsProcessing(false);
    }
  };

  // Generate year options (current year + 10 years)
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 10; i++) {
      const year = (currentYear + i).toString().substr(-2);
      years.push(year);
    }
    return years;
  };

  return (
    <div className="bg-white rounded-lg p-6">
      {/* Card header */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Payment Details</h3>
        <p className="text-gray-600">
          Complete your purchase of <span className="font-medium">{(amount / 100).toFixed(2)} {currency}</span>
        </p>
      </div>

      {/* Error message */}
      {errors.general && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {errors.general}
        </div>
      )}

      {/* Payment form */}
      <form onSubmit={handleSubmit}>
        {/* Card number */}
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="cardNumber">
            Card Number
          </label>
          <input
            className={`appearance-none border ${errors.cardNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500`}
            id="cardNumber"
            type="text"
            placeholder="1234 5678 9012 3456"
            value={cardNumber}
            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
            maxLength="19"
            disabled={isProcessing}
          />
          {errors.cardNumber && <p className="text-red-500 text-xs mt-1">{errors.cardNumber}</p>}
        </div>

        {/* Cardholder name */}
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="cardName">
            Name on Card
          </label>
          <input
            className={`appearance-none border ${errors.cardName ? 'border-red-500' : 'border-gray-300'} rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500`}
            id="cardName"
            type="text"
            placeholder="John Smith"
            value={cardName}
            onChange={(e) => setCardName(e.target.value)}
            disabled={isProcessing}
          />
          {errors.cardName && <p className="text-red-500 text-xs mt-1">{errors.cardName}</p>}
        </div>

        {/* Expiry date and CVV */}
        <div className="flex space-x-4 mb-6">
          <div className="w-1/3 relative">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="expiryMonth">
              Month
            </label>
            <select
              className={`appearance-none border ${errors.expiryMonth ? 'border-red-500' : 'border-gray-300'} rounded-lg w-full py-3 px-4 text-gray-700 bg-white leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500`}
              id="expiryMonth"
              value={expiryMonth}
              onChange={(e) => setExpiryMonth(e.target.value)}
              disabled={isProcessing}
              style={{
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: 'none'
              }}
            >
              <option value="">MM</option>
              {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map((month) => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 pt-6">
              <svg className="fill-current h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
            {errors.expiryMonth && <p className="text-red-500 text-xs mt-1">{errors.expiryMonth}</p>}
          </div>

          <div className="w-1/3 relative">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="expiryYear">
              Year
            </label>
            <select
              className={`appearance-none border ${errors.expiryYear ? 'border-red-500' : 'border-gray-300'} rounded-lg w-full py-3 px-4 text-gray-700 bg-white leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500`}
              id="expiryYear"
              value={expiryYear}
              onChange={(e) => setExpiryYear(e.target.value)}
              disabled={isProcessing}
              style={{
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: 'none'
              }}
            >
              <option value="">YY</option>
              {generateYearOptions().map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 pt-6">
              <svg className="fill-current h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
            {errors.expiryYear && <p className="text-red-500 text-xs mt-1">{errors.expiryYear}</p>}
          </div>

          <div className="w-1/3">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="cvv">
              CVV
            </label>
            <input
              className={`appearance-none border ${errors.cvv ? 'border-red-500' : 'border-gray-300'} rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500`}
              id="cvv"
              type="text"
              placeholder="123"
              value={cvv}
              onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
              maxLength="4"
              disabled={isProcessing}
            />
            {errors.cvv && <p className="text-red-500 text-xs mt-1">{errors.cvv}</p>}
          </div>
        </div>

        {/* Secure payment message */}
        <div className="mb-6 flex items-center text-gray-500 text-sm">
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Your payment information is secured with industry-standard encryption
        </div>

        {/* Action buttons */}
        <div className="flex justify-between">
          <button
            type="button"
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`px-6 py-2 bg-purple-600 text-white rounded-lg ${isProcessing ? 'opacity-70 cursor-not-allowed' : 'hover:bg-purple-700'} transition-colors`}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              'Pay Now'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomPaymentForm; 